// AddressesPage.jsx
import React, { useEffect, useState } from "react";
import { Container, Box, Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, TextField, MenuItem, InputAdornment } from "@mui/material";
import AccountSidebarNav from "../../Components/AccountComponents/AccountSideNacv";
import AddressCard from "../../Components/AccountComponents/AddressCard";
import { privateAxios } from "../../../config/apiConfig";
import { createAddress, deleteAddress, getUserActiveAddress, updateAddress } from "../../State/Address/Action";
import { useDispatch, useSelector } from "react-redux";
import { useConvertPrice } from "../../../Hooks/useConvertPrice";
import EmptyState from "../../Components/AccountComponents/EmptyState";
import countryData from '../../../config/country-phone-code-and-length.json';

export default function Address() {
    const dispatch = useDispatch();
    const jwt = localStorage.getItem("jwt");
    const { address } = useSelector((store) => store);

    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [deletedAddressId, setDeletedAddressId] = useState(null);
    const [open, setOpen] = useState(false);
    const [zipError, setZipError] = useState(""); // error msg store
    const [postOffice, setPostOffice] = useState([]);
    const { selectedCountry } = useConvertPrice();

    const handleClickOpen = (addressId) => {
        setOpen(true);
        setDeletedAddressId(addressId)
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirmRemove = () => {
        dispatch(deleteAddress(deletedAddressId));
        setOpen(false);
    };

    const [formData, setFormData] = useState({
        name: "",
        addressLine1: "",
        addressLine2: "",
        mobile: "",
        country: "",
        city: "",
        state: "",
        zipcode: "",
        postOffice: "",
        nearestLocation: ""
    });

    const [country, setCountry] = useState(countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
        countryData.find(c => c.code === "IN"));
    const [phone, setPhone] = useState("");
    const [error, setError] = useState("");

    const handleCountryChange = (e) => {
        const selected = countryData.find(c => c.code === e.target.value);
        setCountry(selected);
        const fullNumber = `${selected.phone} ${phone}`;
        setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        // ✅ Determine required phone length (local number length only)
        let requiredLength = null;

        if (selected && phone.trim()) {
            if (selected.phoneLength) {
                requiredLength = selected.phoneLength; // number or array
            } else if (selected.min && selected.max) {
                requiredLength = { min: selected.min, max: selected.max };
            }
        }

        // ✅ Validate local number
        let isValid = true;

        if (Array.isArray(requiredLength)) {
            isValid = requiredLength.includes(phone.length);
        } else if (typeof requiredLength === "number") {
            isValid = phone.length === requiredLength;
        } else if (requiredLength?.min && requiredLength?.max) {
            isValid = phone.length >= requiredLength.min && phone.length <= requiredLength.max;
        }

        if (!isValid) {
            setError(
                `Phone number must be ${Array.isArray(requiredLength)
                    ? requiredLength.join(" or ")
                    : requiredLength?.min
                        ? `${requiredLength.min}-${requiredLength.max}`
                        : requiredLength
                } digits`
            );
        } else {
            setError("");
            // ✅ Send full number to backend
            setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        }
    };

    const handlePhoneChange = (e) => {
        // allow only digits for mobile & zipcode
        if (e.target.name === "mobile" && !/^\d*$/.test(e.target.value)) return;
        const cleaned = e.target.value.replace(/\D/g, ""); // ✅ Only digits
        // ✅ Construct full number (+countryCode + localNumber)
        const fullNumber = `${country.phone} ${cleaned}`;
        setPhone(e.target.value);
        // ✅ Determine required phone length (local number length only)
        let requiredLength = null;

        if (country && cleaned.trim()) {
            if (country.phoneLength) {
                requiredLength = country.phoneLength; // number or array
            } else if (country.min && country.max) {
                requiredLength = { min: country.min, max: country.max };
            }
        }

        // ✅ Validate local number
        let isValid = true;

        if (Array.isArray(requiredLength)) {
            isValid = requiredLength.includes(cleaned.length);
        } else if (typeof requiredLength === "number") {
            isValid = cleaned.length === requiredLength;
        } else if (requiredLength?.min && requiredLength?.max) {
            isValid = cleaned.length >= requiredLength.min && cleaned.length <= requiredLength.max;
        }

        if (!isValid) {
            setError(
                `Phone number must be ${Array.isArray(requiredLength)
                    ? requiredLength.join(" or ")
                    : requiredLength?.min
                        ? `${requiredLength.min}-${requiredLength.max}`
                        : requiredLength
                } digits`
            );
        } else {
            setError("");
            // ✅ Send full number to backend
            setFormData((prev) => ({ ...prev, mobile: fullNumber }));
        }

    };

    useEffect(() => {
        if (jwt) dispatch(getUserActiveAddress());
    }, [dispatch, jwt, address?.createAddress, address?.updateAddress, address?.removeAddress]);

    const handleOpenAddressForm = () => {
        setShowForm(true)
        if (selectedCountry) {
            setFormData((prev) => ({
                ...prev,
                country: selectedCountry,
            }));
            setCountry(
                countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
                countryData.find(c => c.code === "IN")
            );
        }
    }

    const handleInputChange = async (e) => {
        const { name, value } = e.target;

        // allow only digits for mobile & zipcode
        if (name === "zipcode" && !/^\d*$/.test(value)) return;

        // update state immediately
        setFormData((prev) => ({ ...prev, [name]: value }));

        // zipcode check after 6 digits
        if (name === "zipcode" && value.length === 6 && formData?.country?.toLowerCase() === "india") {
            try {
                setLoading(true)
                const res = await privateAxios.get(`user/address/check/${value}`);
                const data = res.data;

                if (!data.serviceable) {
                    setZipError("This zipcode is not serviceable.");
                    setPostOffice([]);
                    setFormData((prev) => ({
                        ...prev,
                        city: "",
                        state: "",
                    }));
                    setLoading(false)
                } else {
                    setZipError("");
                    setPostOffice(data.postOffice || []);

                    // ✅ use data.postOffice instead of postOffice state
                    const firstPO = data.postOffice?.[0];
                    if (firstPO) {
                        setFormData((prev) => ({
                            ...prev,
                            city: firstPO.District,
                            state: firstPO.State,
                            country: firstPO.Country || selectedCountry,
                        }));
                    }
                    setLoading(false)
                }
            } catch (error) {
                setZipError("This zipcode is not serviceable.");
                setPostOffice([]);
                setFormData((prev) => ({
                    ...prev,
                    city: "",
                    state: "",
                }));
                setLoading(false)
            }
        } else if (name === "zipcode") {
            setZipError(""); // clear error if not 6 digits yet
            setPostOffice([]);
        }

        if (name === "postOffice") {
            // ✅ use e.target.value instead of stale formData.postOffice
            const selectedPO = postOffice?.find((p) => p.Name === value);
            if (selectedPO) {
                setFormData((prev) => ({
                    ...prev,
                    city: selectedPO.District,
                    state: selectedPO.State,
                    country: selectedPO.Country || selectedCountry,
                }));
            }
        }
    };

    const handleAddOrUpdate = (e) => {
        e.preventDefault();
        if (!error && phone?.trim() !== "") {
            if (editingId) dispatch(updateAddress(editingId, formData, handleCloseAddressForm));
            else dispatch(createAddress(formData, handleCloseAddressForm));
        }
    };

    const handleCloseAddressForm = () => {
        setFormData({ name: "", addressLine1: "", addressLine2: "", mobile: "", country: "", city: "", state: "", zipcode: "", postOffice: "", nearestLocation: "" });
        setEditingId(null);
        setShowForm(false);
        setZipError("");
        setPostOffice([]);
        setError("");
        setPhone("");
        setCountry(
            countryData.find(c => selectedCountry && c.label.includes(selectedCountry)) ||
            countryData.find(c => c.code === "IN")
        );
    }

    const handleEdit = async (address) => {
        const res = await privateAxios.get(`user/address/check/${address.zipcode}`);
        const data = res.data;
        if (data.serviceable) {
            setPostOffice(data.postOffice || []);
        }
        setFormData({
            name: address.name,
            addressLine1: address.addressLine1,
            addressLine2: address?.addressLine2,
            mobile: address.mobile,
            country: address.country,
            city: address.city,
            state: address.state,
            zipcode: address.zipcode,
            postOffice: address.postOffice,
            nearestLocation: address.nearestLocation
        });
        setEditingId(address.id);
        setShowForm(true);
        setZipError("");
        setError("");
        setPhone(
            address.mobile
                ? address.mobile.includes(' ')
                    ? address.mobile.slice(address.mobile.indexOf(' ') + 1)
                    : address.mobile // space नहीं है तो पूरा number use करें
                : '' // null या undefined के लिए empty string
        );

        setCountry(
            // countryData.find(c => address.country && c.label.includes(address.country))
            address.mobile
                ? countryData.find(
                    (c) => c.phone === address.mobile.split(' ')[0].replace('+', '')
                )
                : null
        );
    };

    return (
        <Container maxWidth={false} sx={{
            py: 2,
            // Responsive maxWidth for different screen sizes
            maxWidth: {
                xs: '100%',  // Mobile
                sm: '95%',   // Small tablets
                md: '92%',   // Tablets and small laptops
                lg: '90%',   // Large screens
                xl: '90%',   // Very large displays
            },
            mx: 'auto', // Center align container
        }}>
            {/* Breadcrumb */}
            <Box
                sx={{
                    mb: 2,
                    pb: 2,
                    borderBottom: "1px solid #e5e5e5",
                }}
            >
                <Typography
                    sx={{
                        textTransform: "uppercase",
                        fontSize: "12px",
                        letterSpacing: "0.05em",
                        color: "text.secondary",
                    }}
                >
                    <span style={{ color: "#000" }}>My Account</span>
                    <span style={{ margin: "0 8px" }}>{">"}</span>
                    <span style={{ color: "#000" }}>Manage Addresses</span>
                </Typography>
            </Box>

            {/* Main Section */}
            <Grid container spacing={4}>
                {/* Sidebar */}
                <Grid item xs={12} md={3}>
                    <AccountSidebarNav active="Manage Addresses" />
                </Grid>

                {/* Content */}
                {address?.addresses?.filter(addr => addr?.active)?.length > 0 ? (
                    <Grid item xs={12} md={9}>
                        <Box
                            sx={{
                                mb: 3,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    textTransform: "uppercase",
                                    fontWeight: 600,
                                    letterSpacing: "0.05em",
                                    color: "text.primary",
                                    fontSize: "14px",
                                }}
                            >
                                My Address Book
                            </Typography>

                            <Button
                                onClick={handleOpenAddressForm}
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.05em",
                                    color: "red",
                                    "&:hover": { color: "#cc0000", backgroundColor: "white" },
                                }}
                            >
                                + Add New Address
                            </Button>
                        </Box>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            {address?.addresses?.map(
                                (address) =>
                                    address.active && (
                                        <AddressCard
                                            key={address.id}
                                            address={address}
                                            onEdit={() => handleEdit(address)}
                                            onRemove={() => handleClickOpen(address.id)}
                                        />
                                    )
                            )}
                        </Box>
                    </Grid>
                ) : (
                    <Grid item xs={12} md={9}>
                        <EmptyState
                            imageSrc="https://img.perniaspopupshop.com/ppus-assets/icons/no-addresses-found12_10_21.svg"
                            imageAlt="No Address"
                            title="No address available!"
                            subtitle="Create or update your shipping address."
                            buttonText="Start Shopping"
                            buttonHref="/"
                        />
                    </Grid>
                )}

            </Grid>

            {/* Dialog */}
            <Dialog
                open={showForm}
                onClose={handleCloseAddressForm}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        border: "1px solid #e5e7eb",
                    },
                }}
            >
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div className="relative w-full max-w-2xl bg-white p-8">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseAddressForm}
                            className="absolute right-4 top-4 text-2xl text-neutral-400 hover:text-neutral-600 hover:bg-white"
                            aria-label="Close"
                        >
                            ×
                        </button>

                        {/* Heading */}
                        <h2 className="mb-6 text-sm font-semibold uppercase tracking-wide text-neutral-800">
                            {editingId ? "Edit" : "Add"} Address
                        </h2>

                        {/* ✅ Loader Overlay */}
                        {loading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    bgcolor: "rgba(255,255,255,0.6)", // transparent white overlay
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    zIndex: 2,
                                    borderRadius: "16px",
                                }}
                            >
                                <CircularProgress size={50} thickness={4} />
                            </Box>
                        )}
                        <Box
                            component="form"
                            onSubmit={handleAddOrUpdate}
                            sx={{ py: 2 }}
                        >
                            <Grid container spacing={2}>
                                {/* Form Fields */}
                                {[
                                    { label: "Name", name: "name" },
                                    { label: "Mobile", name: "mobile" },
                                    { label: "Country", name: "country", readOnly: true },
                                    { label: "Zipcode", name: "zipcode", maxLength: 6 },
                                    { label: "City", name: "city", readOnly: formData?.country?.toLowerCase() === "india" ? true : false },
                                    { label: "State", name: "state", readOnly: formData?.country?.toLowerCase() === "india" ? true : false },
                                    { label: "Post Office", name: "postOffice", type: "select" },
                                    { label: "Address Line 1", name: "addressLine1" },
                                    { label: "Address Line 2", name: "addressLine2" },
                                    // 👇 Country dropdown added here
                                    { label: "Near Famous Location", name: "nearestLocation" },
                                ].map(({ label, name, maxLength, type, readOnly }) => (
                                    <Grid item xs={12} sm={6} key={name}>
                                        {type === "select" && formData?.country?.toLowerCase() === "india" ? (
                                            <TextField
                                                select
                                                fullWidth
                                                label={label}
                                                name={name}
                                                size="small"
                                                value={formData[name] || ""}
                                                onChange={handleInputChange}
                                                required
                                                variant="outlined"
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "0",
                                                        fontSize: "0.875rem",
                                                        "&:hover fieldset": { borderColor: "#d1d5db" },
                                                        "&.Mui-focused fieldset": { borderColor: "#374151" },
                                                    },
                                                }}
                                            >
                                                {postOffice?.map((p) => (
                                                    <MenuItem key={p.Name} value={p.Name}>
                                                        {p.Name}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : label === "Mobile" ? (
                                            <TextField
                                                fullWidth
                                                label={label}
                                                name={name}
                                                value={phone}
                                                onChange={handlePhoneChange}
                                                placeholder="Enter mobile number"
                                                variant="outlined"
                                                size="small"
                                                required
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <TextField
                                                                select
                                                                value={country?.code}
                                                                onChange={handleCountryChange}
                                                                variant="standard"
                                                                size="small"
                                                                sx={{
                                                                    "& .MuiOutlinedInput-root": {
                                                                        borderRadius: "0",
                                                                        fontSize: "0.875rem",
                                                                        "&:hover fieldset": { borderColor: "#d1d5db" },
                                                                        "&.Mui-focused fieldset": { borderColor: "#374151" },
                                                                    },
                                                                }}
                                                                InputProps={{ disableUnderline: true }}
                                                            >
                                                                {countryData.map((c) => (
                                                                    <MenuItem key={c.code} value={c.code}>
                                                                        {c.code} (+{c.phone})
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                error={error}
                                                helperText={error !== "" ? error : ""}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "0",
                                                        fontSize: "0.875rem",
                                                        "&:hover fieldset": { borderColor: "#d1d5db" },
                                                        "&.Mui-focused fieldset": { borderColor: "#374151" },
                                                    },
                                                }}
                                            />
                                        ) : (
                                            <TextField
                                                fullWidth
                                                label={label}
                                                name={name}
                                                disabled={readOnly}
                                                variant="outlined"
                                                size="small"
                                                value={formData[name]}
                                                onChange={handleInputChange}
                                                required={name !== "addressLine2"}
                                                error={name === "zipcode" ? Boolean(zipError) : ""}
                                                helperText={name === "zipcode" ? zipError : ""}
                                                inputProps={maxLength ? { maxLength } : undefined}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "0",
                                                        fontSize: "0.875rem",
                                                        "&:hover fieldset": { borderColor: "#d1d5db" },
                                                        "&.Mui-focused fieldset": { borderColor: name === "zipcode" ? zipError ? "red" : "#374151" : "" },
                                                    },
                                                }}
                                            />
                                        )}
                                    </Grid>
                                ))}

                                <Grid item xs={12} mt={1}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Button
                                                type="submit"
                                                fullWidth
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: "#374151",
                                                    color: "#fff",
                                                    fontWeight: "600",
                                                    borderRadius: "8px",
                                                    py: 1,
                                                    fontSize: "0.875rem",
                                                    "&:hover": {
                                                        backgroundColor: "#1f2937",
                                                        boxShadow: "0 4px 8px 0 rgba(55, 65, 81, 0.3)",
                                                        transform: "translateY(-1px)",
                                                    },
                                                    transition: "all 0.2s ease-in-out",
                                                }}
                                            >
                                                {address?.actionLoading ? (
                                                    <CircularProgress
                                                        size={22}
                                                        sx={{
                                                            color: "white", // optional: match your theme
                                                        }}
                                                    />
                                                ) : "Address"}
                                            </Button>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                onClick={handleCloseAddressForm}
                                                sx={{
                                                    py: 1,
                                                    borderRadius: "8px",
                                                    fontWeight: "600",
                                                    fontSize: "0.875rem",
                                                    color: "#6b7280",
                                                    borderColor: "#d1d5db",
                                                    "&:hover": {
                                                        backgroundColor: "#f3f4f6",
                                                        borderColor: "#9ca3af",
                                                    },
                                                    transition: "all 0.2s ease-in-out",
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>

                    </div>
                </div>
            </Dialog>

            {/* Confirm Delete Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        minWidth: "350px"
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: "600", color: "#1f2937", fontSize: "1rem" }}>
                    Confirm Removal
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
                        Are you sure you want to remove this Address?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button
                        onClick={handleClose}
                        size="small"
                        sx={{
                            color: "#6b7280",
                            fontWeight: "500",
                            fontSize: "0.875rem",
                            "&:hover": {
                                backgroundColor: "#f3f4f6",
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRemove}
                        variant="contained"
                        size="small"
                        sx={{
                            backgroundColor: "#374151",
                            color: "white",
                            fontWeight: "600",
                            borderRadius: "6px",
                            px: 2,
                            fontSize: "0.875rem",
                            "&:hover": {
                                backgroundColor: "#1f2937",
                            }
                        }}
                        autoFocus
                    >
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
